// Spots contact details people try to share before a booking is confirmed:
// phone numbers (any format, incl. +61 and written-out digits), emails
// (incl. "name at gmail dot com"), "text/call me", WhatsApp/Facebook/
// Marketplace/Instagram etc, @handles and outside links. Tuned so prices,
// dates, postcodes and sizes ("$120", "12/11", "4000", "6ft") don't trigger.
// Kept free of React so it can be unit tested (contactDetection.test.js).

// 'warn' — show a warning and log it, but still let it through (current Terms).
// 'block' — replace details with [contact removed] and stop messages sending.
// Switch to 'block' when the new Terms go live.
export const CONTACT_MODE = 'warn'

// Wanted posts are public pages, so they're blocked now (title + note):
// the post can't go up until phone numbers, emails, links and socials are
// removed. "Call me" on its own (no actual details) is warned, not blocked.
// The database refuses obvious phone numbers and emails too.
export const WANTED_CONTACT_MODE = 'block'
export const BLOCKING_KINDS = ['phone', 'email', 'link', 'social']
export const blockingKinds = (result) => (result?.kinds || []).filter(k => BLOCKING_KINDS.includes(k))

export const CONTACT_WARNING = 'For your safety, keep contact details off HireIt until a booking is confirmed.'

const WORD_DIGITS = { zero: '0', oh: '0', o: '0', nought: '0', one: '1', won: '1', two: '2', too: '2', to: '2', three: '3', four: '4', for: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9', niner: '9' }

const PATTERNS = [
  // Emails: plain, and "name at gmail dot com" / "name (at) gmail (dot) com"
  { kind: 'email', re: /[a-z0-9._%+-]+\s*@\s*[a-z0-9-]+(?:\s*\.\s*[a-z]{2,})+/gi },
  { kind: 'email', re: /[a-z0-9._%+-]+\s*[([{]?\s*\bat\b\s*[)\]}]?\s*[a-z0-9-]+\s*[([{]?\s*\b(?:dot|\.)\s*[)\]}]?\s*(?:com|net|org|au|co|io|me)\b/gi },
  { kind: 'email', re: /\b(?:gmail|hotmail|outlook|yahoo|bigpond|icloud|live|proton(?:mail)?)\s*(?:\.|dot)\s*(?:com|net|au)\b/gi },
  // Outside links
  { kind: 'link', re: /\bhttps?:\/\/\S+/gi },
  { kind: 'link', re: /\bwww\s*\.\s*\S+/gi },
  { kind: 'link', re: /\b[a-z0-9-]{2,}\.(?:com|net|org|io|me|co)(?:\.au)?\b(?:\/\S*)?/gi },
  { kind: 'link', re: /\b[a-z0-9-]{2,}\.com?\.au\b/gi },
  // Apps and social
  { kind: 'social', re: /\b(?:whats\s*app|whatsapp|wa\.me|telegram|signal app|snap\s*chat|snapchat|instagram|insta|ig handle|tik\s*tok|facebook|fb|messenger|marketplace|gumtree|wechat|viber|kik|linkedin)\b/gi },
  { kind: 'social', re: /(?:^|[\s(])@[a-z0-9_.]{3,}\b/gi },
  // "text me", "call me", "ring me on", "sms me", "message me on", "my number is", "my mobile"
  { kind: 'call_me', re: /\b(?:text|txt|call|ring|sms|phone|msg|message|dm|pm|contact)\s+(?:me|us)\b(?:\s+(?:on|at|direct(?:ly)?))?/gi },
  { kind: 'call_me', re: /\bmy\s+(?:number|no\.?|mobile|mob|cell|ph|phone|email|e-mail)\b/gi },
]

// Phone numbers: any run of digits (with spaces, dots, dashes, brackets, +)
// that adds up to 8+ digits. Australian numbers are 9–10 digits (+61 → 11).
function findPhoneNumbers(text) {
  const found = []
  const re = /\+?\(?\d[\d\s().\-–]{6,}\d/g
  let m
  while ((m = re.exec(text))) {
    const digits = m[0].replace(/\D/g, '')
    // Dates like 12/11/2026 or 12-11-2026 use / or - between short groups
    if (/^\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}$/.test(m[0].trim())) continue
    if (digits.length >= 8 && digits.length <= 13) found.push(m[0].trim())
  }
  return found
}

// "oh four one two three four five six seven eight" or mixed "04 one two…"
function findWrittenNumbers(text) {
  const tokens = text.split(/[\s,.\-–]+/).filter(Boolean)
  const found = []
  let run = []
  let hasWord = false
  const flush = () => {
    const digits = run.map(r => r.digit).join('')
    if (digits.length >= 8 && hasWord) found.push(run.map(r => r.text).join(' '))
    run = []
    hasWord = false
  }
  for (const t of tokens) {
    const lower = t.toLowerCase()
    if (/^\d+$/.test(t)) run.push({ text: t, digit: t })
    else if (WORD_DIGITS[lower] !== undefined) { run.push({ text: t, digit: WORD_DIGITS[lower] }); hasWord = true }
    else flush()
  }
  flush()
  return found
}

// Returns { found, kinds: ['phone', 'email', …], matches: [...] }
export function detectContactDetails(text) {
  const s = String(text || '')
  if (!s.trim()) return { found: false, kinds: [], matches: [] }
  const matches = []
  const kinds = new Set()
  for (const p of PATTERNS) {
    for (const m of s.match(p.re) || []) { kinds.add(p.kind); matches.push(m.trim()) }
  }
  for (const m of findPhoneNumbers(s)) { kinds.add('phone'); matches.push(m) }
  for (const m of findWrittenNumbers(s)) { kinds.add('phone'); matches.push(m) }
  return { found: matches.length > 0, kinds: [...kinds], matches: [...new Set(matches)] }
}

// For 'block' mode later: swap anything detected for [contact removed]
export function removeContactDetails(text) {
  let out = String(text || '')
  const { matches } = detectContactDetails(out)
  for (const m of matches.sort((a, b) => b.length - a.length)) out = out.split(m).join('[contact removed]')
  return out
}

// A short piece of text around the first match, for the admin Flags tab
export function contactExcerpt(text, matches) {
  const s = String(text || '')
  const first = matches?.[0]
  const i = first ? s.indexOf(first) : -1
  if (i < 0) return s.slice(0, 200)
  const start = Math.max(0, i - 60)
  return (start > 0 ? '…' : '') + s.slice(start, i + first.length + 60) + (i + first.length + 60 < s.length ? '…' : '')
}

// Plain-English labels for the admin Flags tab
export const CONTACT_KIND_LABELS = { phone: 'Phone number', email: 'Email', link: 'Outside link', social: 'Social / app', call_me: '"Call/text me"' }
